import {
  addDoc,
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  type DocumentData,
  type QueryDocumentSnapshot
} from "firebase/firestore";
import { firestore } from "@/config/firebase";

export type UserActivityType = "project" | "team" | "document" | "notification" | "support";

export type UserActivity = {
  id: string;
  type: UserActivityType;
  title: string;
  description?: string;
  path: string;
  entityId?: string;
  organizationId?: string;
  createdAt?: unknown;
};

export type RecordUserActivityInput = Omit<UserActivity, "id" | "createdAt">;

const fallbackType: UserActivityType = "project";

function userActivityRef(uid: string) {
  return collection(firestore, "users", uid, "activity");
}

function toActivity(snapshot: QueryDocumentSnapshot<DocumentData>): UserActivity {
  const data = snapshot.data();
  const rawType: unknown = data.type;
  const type: UserActivityType =
    rawType === "project" ||
    rawType === "team" ||
    rawType === "document" ||
    rawType === "notification" ||
    rawType === "support"
      ? rawType
      : fallbackType;

  return {
    id: snapshot.id,
    type,
    title: typeof data.title === "string" ? data.title : "Atividade",
    description: typeof data.description === "string" ? data.description : undefined,
    path: typeof data.path === "string" ? data.path : "/painel",
    entityId: typeof data.entityId === "string" ? data.entityId : undefined,
    organizationId: typeof data.organizationId === "string" ? data.organizationId : undefined,
    createdAt: data.createdAt
  };
}

export function subscribeToUserActivity(
  uid: string,
  onChange: (activity: UserActivity[]) => void,
  onError: (error: Error) => void
) {
  const activityQuery = query(userActivityRef(uid), orderBy("createdAt", "desc"), limit(40));

  return onSnapshot(activityQuery, (snapshot) => onChange(snapshot.docs.map(toActivity)), onError);
}

export async function recordUserActivity(uid: string, input: RecordUserActivityInput) {
  await addDoc(userActivityRef(uid), {
    type: input.type,
    title: input.title,
    description: input.description ?? null,
    path: input.path,
    entityId: input.entityId ?? null,
    organizationId: input.organizationId ?? null,
    createdAt: serverTimestamp()
  });
}
